export interface Suggestion {
  familyKey: string | null;
  ignore?: boolean;
  year?: number | null;
  copiedFrom?: { formId: string; label: string; score: number } | null;
  reason?: string;
}

export interface EventRef {
  id: string;
  title: string;
  startsAt: string | null;
}

export interface EditionSummary {
  id: string;
  formId: string;
  title: string | null;
  label: string | null;
  year: number | null;
  cohort: string | null;
  status: 'new' | 'active' | 'ignored' | 'waiting';
  familyId: string | null;
  event: EventRef | null;
  upstreamActive: boolean | null;
  upstreamEntryCount: number | null;
  upstreamCreatedAt: string | null;
  lastPolledAt: string | null;
  newQuestions: number;
  suggestion: Suggestion | null;
  entries: number;
  thisWeek: number;
  unmatched: number;
  matched: number;
  nameOnly: number;
  nonMember: number;
  flagged: number;
  missing: number;
  lastEntryAt: string | null;
}

export interface FamilyCard {
  key: string;
  name: string;
  identity: 'email' | 'name';
  description: string | null;
  editions: number;
  entries: number;
  thisWeek: number;
  unmatched: number;
  flagged: number;
  missing: number;
  nameOnly: number;
  newQuestions: number;
  lastEntryAt: string | null;
  exceptions: number;
}

export interface FormsOverview {
  enabled: boolean;
  needsDecision: EditionSummary[];
  families: FamilyCard[];
  ignored: { count: number; entries: number };
  waiting: number;
  lastSync: {
    status: string;
    startedAt: string;
    completedAt: string | null;
    recordsProcessed: number;
    errorMessage: string | null;
  } | null;
}

export interface FamilyField {
  key: string;
  label: string;
  type: 'yes/no' | 'choice' | 'number' | 'date' | 'text';
  multiple?: boolean;
  sensitive: boolean;
}

export interface Question {
  id: string;
  label: string;
  section: string | null;
  gfType: string;
  choices: string[] | null;
  multiple: boolean;
  decision: string | null;
  isNew: boolean;
  compatibleFields: string[];
}

export interface EditionDetail extends EditionSummary {
  family: { key: string; name: string } | null;
  fields: FamilyField[];
  questions: Question[];
}

export interface ResponseRow {
  id: string;
  entryId: string;
  formId: string;
  edition: string;
  email: string | null;
  name: string | null;
  submittedAt: string;
  matchStatus: string;
  member: { id: string; firstName: string; lastName: string; email: string } | null;
  event: { id: string; title: string } | null;
  flags: { question: string; field: string; value: unknown; reason: string }[];
  upstreamMissingSince: string | null;
  upstreamMissingRuns: number;
  deliveredBy: string;
}

export const editionName = (e: { label: string | null; title: string | null; formId: string }) =>
  e.label || e.title || `Form ${e.formId}`;

export function apiError(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error ?? fallback;
}

export const FAMILY_EVENT_KEYS = new Set(['event_registration', 'session_feedback']);
